import { DeleteOutlined, EditOutlined, Loading3QuartersOutlined, PlusOutlined, SearchOutlined, ExportOutlined } from '@ant-design/icons'
import { Button, Input, message, Modal, Select, Tag } from 'antd'
import dayjs from 'dayjs'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import { fetchGroupList } from '../../apis/group'
import { delDataSource, delGA4DataSource, fetchDataSource, fetchSources } from '../../apis/source'
import MainTable from '../../components/Basic/MainTable'
import useParams from '../../hook/useParams'
import { SearchWrapper, WhiteWrapper } from './dataSource.styled'
import { Sources } from '../../constants/Sources'

function DataSource (props) {
  const { adminMode } = props
  const { t: ln_t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const { pathname } = location || {}
  const user = useSelector((state) => state.user)
  const { groups, nowGroup } = user || {}
  const params = useParams({ includes: 'creator,dataSourceType,brands,dataSourceSchema,dataSourceSchema.mappingKey', groupId: adminMode ? undefined : nowGroup })
  const { handleFetchParams, fetchParams } = params
  const [typeOptions, setTypeOptions] = useState([])
  const [groupList, setGroupList] = useState([])

  const handleDelete = (id, data) => {
    Modal.confirm({
      title: `${ln_t('util.delete')} ${data.name}`,
      content: `${ln_t('message.confirm.delete')} ${data.name}?`,
      onOk: async () => {
        const delApi = data?.dataSourceType?.name === Sources.GA4 ? delGA4DataSource : delDataSource
        const result = await delApi(id)
        if (result?.status === 204) {
          handleFetchParams('add', {})
          message.success(ln_t('message.success.delete'))
        }
      }
    })
  }

  const apiFunc = async () => fetchDataSource(fetchParams)
  const previewStatus = {
    ready: { title: ln_t('segment.ready'), color: 'gold' },
    doing: { title: ln_t('segment.doing'), color: 'geekblue', icon: <Loading3QuartersOutlined spin /> },
    done: { title: ln_t('segment.done'), color: 'green' },
    error: { title: ln_t('segment.error'), color: 'red' },
    'api error': { title: ln_t('segment.apiError'), color: 'red' }
  }
  const formatDate = (date, detail) => dayjs(date).format(detail ? 'DD-MM-YYYY HH:mm:ss' : 'DD-MM-YYYY')
  const columns = [
    { title: ln_t('util.name'), dataIndex: 'name', sorter: true },
    {
      title: ln_t('util.type'),
      dataIndex: 'type',
      render: (e, data) => data?.dataSourceType?.name || '-',
      filters: typeOptions,
      filterMultiple: false
    },
    {
      title: ln_t('dataSource.details'),
      dataIndex: 'detailName',
      render: (e, data) => data?.dataSourceType?.name === Sources.FIRST_PARTY ? `${e ? `${e}-` : ''}${data?.incrementalType}` : e
    },
    { title: ln_t('dataSource.brands'), dataIndex: 'brands', render: e => e && e.length ? e?.map(i => i.name).join(', ') : '-' },
    {
      title: ln_t('dataSource.status'),
      dataIndex: 'status',
      render: e => {
        const { title, icon, color } = previewStatus[e] || {}
        return <Tag style={{ width: '80px', cursor: 'default', textAlign: 'center' }} color={color} icon={icon}>{title}</Tag>
      }
    },
    { title: ln_t('util.createdAt'), dataIndex: 'createdAt', render: e => formatDate(e), sorter: true },
    { title: ln_t('util.updatedAt'), dataIndex: 'updatedAt', render: e => formatDate(e), sorter: true },
    { title: ln_t('util.creator'), dataIndex: 'creator', render: e => e?.name || '-' },
    {
      title: ln_t('util.action'),
      dataIndex: 'id',
      render: (id, data) => adminMode
        ? <Button size="small" icon={<ExportOutlined />} onClick={() => navigate(`${pathname}/detail`, { state: { data } })}>{ln_t('util.viewDetail')}</Button>
        : <div className="action-btn">
            <Button
              size="small" icon={<EditOutlined />}
              onClick={() => navigate(`${pathname}/edit`, { state: { data } })}
              disabled={data?.dataSourceType?.name === Sources.GA4}
            />
            <Button size="small" icon={<DeleteOutlined />} onClick={() => handleDelete(id, data)} />
          </div>
    }
  ]

  const fetchTypes = async () => {
    const availableSrc = groups?.find(i => i.id === nowGroup)?.dataSourceTypes?.split(',') || []
    const res = await fetchSources({ maxResults: 999 })
    if (res?.status === 200) {
      const type = res?.data?.filter(i => availableSrc.includes(`${i.id}`))?.map(({ id, name }) => ({ value: id, text: name }))
      setTypeOptions(type)
    }
  }
  const getGroups = async () => {
    const res = await fetchGroupList({ maxResults: 999 })
    if (res?.status === 200) setGroupList(res?.data?.map(i => ({ value: i.id, label: i.name })))
  }
  useEffect(() => {
    fetchTypes()
    if (adminMode) getGroups()
  }, [])

  return (
    <WhiteWrapper>
      <h3>{ln_t('sidebar.dataSource')}</h3>
      <SearchWrapper>
        <Input.Search
          allowClear
          style={{ width: '400px' }}
          placeholder={ln_t('util.search')}
          onSearch={e => handleFetchParams('add', { name: `%${e}%` })}
          enterButton={<Button type="primary" size="small" icon={<SearchOutlined />} />}
        />
        {adminMode
          ? <div className="d-flex">
              <div style={{ fontSize: '14px', fontWeight: 500, margin: 'auto 8px' }}>{ln_t('util.group')} :</div>
              <Select
                showSearch
                style={{ minWidth: '120px' }}
                defaultValue="all"
                placeholder={`${ln_t('util.select')} ${ln_t('util.group')}`}
                options={[{ value: 'all', label: 'ALL' }, ...groupList]}
                optionFilterProp="children"
                onChange={e => handleFetchParams('add', { groupId: e === 'all' ? undefined : e })}
              />
            </div>
          : <Button type="default" icon={<PlusOutlined />} onClick={() => navigate(`${pathname}/new`)}>
              {ln_t('util.add')} {ln_t('sidebar.dataSource')}
            </Button>
        }
      </SearchWrapper>

      <MainTable
        columns={columns}
        rowKey="id"
        apiFunc={apiFunc}
        params={params}
        disabledRow={record => !record.previewCount || !record.status}
      />
    </WhiteWrapper>
  )
}

export default DataSource
